import styled from 'styled-components';
import Drawer from '../../../../components/Drawer';
import Button from '../../../../components/Button';
import {
  useGetWorkflowsList,
  useUpdateGroupedWorkflow
} from '../../../../services/workflow.services';
import { useUserGroups } from '../../../../services/user.service';
import Table from '../../../../components/Table';

import dayjs from '../../../../utils/dayjs';
import { useMemo, useState } from 'react';
import Checkbox from '../../../../components/Checkbox';
import toast from 'react-hot-toast';
import { extractApiError } from '../../../../utils';

export function AddAIFM({ isOpen, onClose, workflows, groupId, refetch, groupName }) {
  const { data: groups, isLoading: isLoadingCompany } = useUserGroups();

  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const currentGroupId = groups?.current_company_id;

  const updateGroupedWorkflows = useUpdateGroupedWorkflow();
  const {
    data: projects,
    isLoading,
    refetch: refetchWorkflows
  } = useGetWorkflowsList({
    params: {
      type: '/all',
      headers: {
        company_id: currentGroupId
      }
    },
    queryParams: {
      enabled: !!currentGroupId && isOpen
    }
  });

  const workflowIds = workflows.map(item => item.id);

  const onSelectWorkflow = id => {
    if (selectedWorkflows.includes(id)) {
      setSelectedWorkflows(prev => prev.filter(value => value !== id));
    } else {
      setSelectedWorkflows(prev => [...prev, id]);
    }
  };

  const onCancel = () => {
    onClose();
    setSelectedWorkflows([]);
  };

  const onUpdate = () => {
    updateGroupedWorkflows.mutate(
      {
        id: groupId,
        body: {
          name: groupName,
          workflow_ids: [...workflowIds, ...selectedWorkflows]
        }
      },
      {
        onSuccess: () => {
          refetch();
          onCancel();
          toast.success('Projects have been added');
        },
        onError: res => {
          toast.error(extractApiError(res, 'Unable to add these projects.'));
        }
      }
    );
  };

  const _columns = useMemo(
    () => [
      {
        title: 'Name',
        key: 'name',
        render: data => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Checkbox
                onChange={() => onSelectWorkflow(data.id)}
                checked={selectedWorkflows.includes(data.id)}
                disabled={workflowIds.includes(data.id)}
              />
              <span>{data.name}</span>
            </div>
          );
        },
        tdProps: data => ({
          className: workflowIds.includes(data.id) ? 'disabled-column' : ''
        })
      },
      {
        title: 'Created date',
        key: 'created_at',
        render: data => {
          return dayjs(data.created_at).format('DD-MM-YYYY HH:mm');
        },
        tdProps: data => ({
          className: workflowIds.includes(data.id) ? 'disabled-column' : ''
        })
      },
      {
        title: 'Updated Date',
        key: 'modified_at',
        render: data => {
          return dayjs(data.modified_at).format('DD-MM-YYYY HH:mm');
        },
        tdProps: data => ({
          className: workflowIds.includes(data.id) ? 'disabled-column' : ''
        })
      },
      {
        title: 'Assigned To',
        render: data => data.assigned_user_name || '-',
        tdProps: data => ({
          className: workflowIds.includes(data.id) ? 'disabled-column' : ''
        })
      }
    ],
    [workflowIds, selectedWorkflows]
  );

  return (
    <Drawer
      style={{
        width: '675px'
      }}
      title="Add Projects"
      withContainer={false}
      open={isOpen}
      onClose={onClose}
    >
      <BodyStyle>
        <div className="table">
          {(projects?.length > 0 || isLoading || isLoadingCompany) && (
            <Table
              trClassName="project-table-tr"
              hasIndex={false}
              isLoading={isLoading || isLoadingCompany}
              columns={_columns}
              data={projects}
              className="table"
            />
          )}
        </div>
        <div className="buttons">
          <Button onClick={onCancel} variant="outlined-gray" size="sm">
            Cancel
          </Button>
          <Button
            disabled={selectedWorkflows.length === 0}
            size="sm"
            onClick={onUpdate}
            isLoading={updateGroupedWorkflows.isLoading}
          >
            Add {selectedWorkflows.length > 0 ? `(${selectedWorkflows.length})` : ''}
          </Button>
        </div>
      </BodyStyle>
    </Drawer>
  );
}

const BodyStyle = styled.div`
  margin-top: 16px;
  .table {
    max-height: 80vh;
    overflow: auto;
    .disabled-column {
      background-color: #f9fafb;
      color: #adb5bd;
    }
  }
  .buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 56px;
  }
`;
